/**
 * useTrainingVideos — Hook for training video catalog
 *
 * Fetches video list from the backend API, provides category
 * filtering and title/description search.
 */

import { useState, useMemo, useEffect } from 'react';
import { fetchTrainingVideoList } from '../services/trainingVideoService';
import type { TrainingVideo } from '../types/training';

interface UseTrainingVideosReturn {
    videos: TrainingVideo[];
    filteredVideos: TrainingVideo[];
    categories: string[];
    activeCategory: string;
    searchQuery: string;
    loading: boolean;
    error: string | null;
    setCategory: (cat: string) => void;
    setSearchQuery: (q: string) => void;
}

export function useTrainingVideos(): UseTrainingVideosReturn {
    const [videos, setVideos] = useState<TrainingVideo[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeCategory, setActiveCategory] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);

        fetchTrainingVideoList()
            .then(list => {
                if (!cancelled) {
                    setVideos(list);
                    setLoading(false);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setError('Failed to load training videos');
                    setLoading(false);
                }
            });

        return () => { cancelled = true; };
    }, []);

    // Derive unique categories from backend data
    const categories = useMemo(() => {
        const unique = Array.from(new Set(videos.map(v => v.category)));
        return unique.sort();
    }, [videos]);

    const filteredVideos = useMemo(() => {
        let result = videos;

        if (activeCategory !== 'all') {
            result = result.filter(v => v.category === activeCategory);
        }

        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase().trim();
            result = result.filter(
                v => v.title.toLowerCase().includes(q) || v.description.toLowerCase().includes(q)
            );
        }

        return result;
    }, [videos, activeCategory, searchQuery]);

    return {
        videos,
        filteredVideos,
        categories,
        activeCategory,
        searchQuery,
        loading,
        error,
        setCategory: setActiveCategory,
        setSearchQuery,
    };
}
